import { Component, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { DialogCloseResult, DialogRef, DialogService } from '@progress/kendo-angular-dialog';
import { SortDescriptor } from '@progress/kendo-data-query';
import { filter } from 'rxjs/operators';
import { SageInvoiceReminderModalComponent } from 'src/app/components/shared/bookkeeping/bookkeeping-sage-invoice-list/sage-invoice-reminder-modal/sage-invoice-reminder-modal.component';
import { bookKeepingInvoiceListDelete, bookKeepingInvoiceListUpdate } from 'src/app/shared/helpers/auth-config/bookkeeping.config';
import { SortOrder } from 'src/app/shared/modules/graphql/enums/generic.enums';
import { IApiSageInvoice, IApiSageInvoiceFilter, IApiSageInvoiceOrderBy, IApiUserFilterType } from 'src/app/shared/modules/graphql/types/types';
import { LoaderService } from 'src/app/shared/modules/loader/loader.service';
import { InvoiceService } from 'src/app/shared/services';
import { SageInvoiceDataSource } from 'src/app/shared/services/invoice/sage-invoice.datasource';
import { SageInvoiceAgingReminderSidebarComponent } from 'src/app/components/shared/bookkeeping/bookkeeping-sage-invoice-list/sage-invoice-aging-reminder-sidebar/sage-invoice-aging-reminder-sidebar.component';

@Component({
  selector: 'app-bookkeeping-sage-invoice-list',
  templateUrl: './bookkeeping-sage-invoice-list.component.html',
  styleUrls: ['./bookkeeping-sage-invoice-list.component.scss']
})
export class BookkeepingSageInvoiceListComponent implements OnInit {
  public authConfig = {
    bookKeepingInvoiceListUpdate,
    bookKeepingInvoiceListDelete
  }
  // @ViewChild('filterForm', { static: false }) filterForm: NgForm;
  public filtersActive = false;

  // Table Setup
  private _dataSource: SageInvoiceDataSource;
  public get dataSource() {
    return this._dataSource;
  }
  public set dataSource(val) {
    this._dataSource = val;
  }
  public displayedColumns = ["invoice", "invId", "billTo", "client", "createdBy", "createdAt", "status", "qbErrors", "actions"];
  public pageSize: number = null;
  public totalResults = 0;

  // Filter options
  public searchValue: string = null;
  public invoiceNumber: number = null;
  public investigationId: string = null;
  public createdBy = null;
  public startDate = null;
  public endDate = null;

  // Select options - need to be fetched
  public invoices = null;
  public investigations = null;
  public orderByOptions = IApiSageInvoiceOrderBy;
  public userViewFilter = IApiUserFilterType.ViewStaffUser;
  public sort: SortDescriptor[] = [
    {
      field: IApiSageInvoiceOrderBy.DueDate,
      dir: "asc",
    },
  ];

  public selectAll: boolean = false;
  public selected: IApiSageInvoice[] = [];
  public selectAllInterminate: boolean = false;
  public invoiceList: IApiSageInvoice[] = [];
  public totalAmountDue: number = 0;

  public get pageOptions() {
    if (!this._dataSource) return null;
    return this._dataSource.listPage;
  }

  constructor(
    private invoiceService: InvoiceService,
    private loader: LoaderService,
    private dialogService: DialogService
  ) {
    this.dataSource = new SageInvoiceDataSource(this.invoiceService);
    this.dataSource.listPage.orderBy = IApiSageInvoiceOrderBy.Id;
    this.dataSource.listPage.sortOrder = SortOrder.DESC;
  }

  ngOnInit() {
    this.loader.attachObservable(this.dataSource.loading$);
    this.load();
  }

  public load(reset = true) {
    this.selected = [];
    this.selectAll = false;
    this.selectAllInterminate = false;
    if (reset)
      this.dataSource.pagingReset();
    this.dataSource.load().then(() => {
      this.dataSource?.contents$.subscribe((res) => {
        const nowUtc = Date.now();
        this.invoiceList = (res || []).map(invoice => {
          const parsed = invoice.invoiceDate ? new Date(invoice.invoiceDate) : null;
          const ageingDays = parsed && !Number.isNaN(parsed.getTime())
            ? Math.floor((nowUtc - Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate())) / 86400000)
            : null;
          return { ...invoice, ageingDays };
        });
      });
    });
    this.invoiceService.sageTotalAmountDue(this.dataSource.lastFilters, {
      ...this.dataSource.listPage,
      offset: 0,
      limit: 25
    }).subscribe((res) => {
      this.totalAmountDue = res;
    });
  }

  public selectAllCheckboxChange(e) {
    if (e.target.checked) {
      this.selected = [...this.invoiceList];
    } else {
      this.selected = [];
    }
    this.selectAllInterminate = false;
  }

  public checkIfSelected(id: string): boolean {
    return this.selected.some(item => item.id === id);
  }

  singleCheckboxChecked(checked: boolean, dataItem: any) {
    if (checked) {
      if (!this.checkIfSelected(dataItem?.id)) {
        this.selected.push(dataItem);
      }
    } else {
      this.selected = this.selected.filter(item => item.id !== dataItem?.id);
    }
    this.selectAll = this.selected.length === this.invoiceList.length ? true : false;
    this.selectAllInterminate = !this.selected.length ? false : this.selected.length !== this.invoiceList.length ? true : false;
  }

  public clear(): void {
    this.selected = [];
    this.selectAll = false;
    this.selectAllInterminate = false;
  }

  public sortChange = (e) => {
    this.sort = e;
    if (e && e?.[0]?.dir) {
      this.dataSource.listPage.orderBy = e?.[0]?.field;
      this.dataSource.listPage.sortOrder =
        e?.[0]?.dir === "asc" ? SortOrder.ASC : SortOrder.DESC;
    } else {
      this.dataSource.listPage.orderBy = IApiSageInvoiceOrderBy.DueDate;
      this.dataSource.listPage.sortOrder = SortOrder.ASC;
    }
    this.load();
  };

  public changeFilters(filters: IApiSageInvoiceFilter[]) {
    this.dataSource?.lastFilters?.map(filter => this.dataSource.applyFilter(filter?.type, null));
    filters?.map(filter => this.dataSource.applyFilter(filter?.type, filter?.value));
    this.dataSource.pagingReset();
    this.load();
  }

  openReminderModal() {
    const dialog: DialogRef = this.dialogService.open({
      content: SageInvoiceReminderModalComponent,
      width: "60%",
      maxWidth: "60%",
      height: "auto",
      preventAction: (ev) => {
        return ev !== 'closed' as any;
      },
    });

    const dialogInstance = dialog.content.instance as SageInvoiceReminderModalComponent;
    dialogInstance.invoices = this.selected;

    dialog.result.pipe(
      filter((v) => !!v)
    ).subscribe((result: DialogCloseResult) => {
      if (result === true) this.load(false);
    });
  }

  public openAgingReminderSidebar(dataItem: IApiSageInvoice) {
    const dialog: DialogRef = this.dialogService.open({
      content: SageInvoiceAgingReminderSidebarComponent,
      width: "40%",
      height: "100vh",
      cssClass: 'right-position history-log',
      preventAction: (ev) => {
        return ev !== 'closed' as any;
      },
    });

    const dialogInstance = dialog.content.instance as SageInvoiceAgingReminderSidebarComponent;
    dialogInstance.data = {
      sageInvoiceId: dataItem.id,
      invoiceNumber: dataItem.invoiceNumber
    };
  }

}
